import { intersection, orderBy, spread } from "lodash";
import { createSelector } from "reselect";

import { SaleSearchFields } from "constants/globalSearch";

import { resolveSaleStatus } from "lib/saleStatus";

import {
  selectEffectiveEContractStatusBySaleIdLookup,
  selectEffectiveSaleTitleBySaleIdLookup,
} from "../sales";
import { getActiveRole, getSales, getSaleStatuses } from "../root";
import { getSaleSearchBySearchField } from "../settings";

const applyFilter = (sales, filterField, filterValues) => {
  const selectedSaleIds = Object.values(sales).map(
    sale => sale.livestocksale_id,
  );
  const filteredSaleIds = filterValues
    ? selectedSaleIds.filter(saleId =>
        filterValues.includes(sales[saleId][filterField]),
      )
    : selectedSaleIds;
  return filteredSaleIds;
};

const createFilterSelector = (filterField, searchField) =>
  createSelector(
    getSales,
    getSaleSearchBySearchField(searchField),
    (sales, filterValues) => applyFilter(sales, filterField, filterValues),
  );

export const selectSaleIdFilteredSaleIds = createFilterSelector(
  "livestocksale_id",
  SaleSearchFields.SaleId,
);

export const selectSpeciesFilteredSaleIds = createFilterSelector(
  "species_id",
  SaleSearchFields.Species,
);
export const selectSaleyardFilteredSaleIds = createFilterSelector(
  "saleyard_name",
  SaleSearchFields.Saleyard,
);
export const selectSaleTypeFilteredSaleIds = createFilterSelector(
  "sale_type",
  SaleSearchFields.SaleType,
);
export const selectSaleTitleFilteredSaleIds = createSelector(
  [
    getSales,
    selectEffectiveSaleTitleBySaleIdLookup,
    getSaleSearchBySearchField(SaleSearchFields.Title),
  ],
  (sales, effectiveTitleBySaleId, filterValues) => {
    const selectedSaleIds = Object.values(sales).map(
      sale => sale.livestocksale_id,
    );
    if (!filterValues || filterValues.length === 0) {
      return selectedSaleIds;
    }
    return selectedSaleIds.filter(saleId =>
      filterValues.includes(effectiveTitleBySaleId[saleId]),
    );
  },
);
export const selectSaleDateFilteredSaleIds = createFilterSelector(
  "date",
  SaleSearchFields.Date,
);

// Managers / assignees are a list of user ids per sale, so a sale matches if any
// of its role user ids is in the selected filter values.
const applySaleRoleFilter = (sales, idField, filterValues) => {
  const allSaleIds = Object.values(sales).map(sale => sale.livestocksale_id);
  if (!filterValues || filterValues.length === 0) {
    return allSaleIds;
  }
  return Object.values(sales)
    .filter(sale =>
      (sale.sale_attributes?.[idField] || []).some(id =>
        filterValues.includes(id),
      ),
    )
    .map(sale => sale.livestocksale_id);
};

export const selectSaleManagersFilteredSaleIds = createSelector(
  getSales,
  getSaleSearchBySearchField(SaleSearchFields.Managers),
  (sales, filterValues) =>
    applySaleRoleFilter(sales, "managerIds", filterValues),
);

export const selectSaleAssigneesFilteredSaleIds = createSelector(
  getSales,
  getSaleSearchBySearchField(SaleSearchFields.Assignees),
  (sales, filterValues) =>
    applySaleRoleFilter(sales, "assigneeIds", filterValues),
);

// eContract status lives on the embedded sale_attributes (plus a computed
// default), not a flat field on the sale, so it needs its own filter rather
// than createFilterSelector.
const applyEContractStatusFilter = (
  sales,
  filterValues,
  effectiveEContractStatusBySaleIdLookup,
) => {
  const allSaleIds = Object.values(sales).map(sale => sale.livestocksale_id);
  if (!filterValues || filterValues.length === 0) {
    return allSaleIds;
  }
  return allSaleIds.filter(saleId =>
    filterValues.includes(effectiveEContractStatusBySaleIdLookup[saleId]),
  );
};

export const selectEContractStatusFilteredSaleIds = createSelector(
  getSales,
  getSaleSearchBySearchField(SaleSearchFields.EContractStatus),
  selectEffectiveEContractStatusBySaleIdLookup,
  (sales, filterValues, effectiveEContractStatusBySaleIdLookup) =>
    applyEContractStatusFilter(
      sales,
      filterValues,
      effectiveEContractStatusBySaleIdLookup,
    ),
);

export const selectSaleStatusFilteredSaleIds = createSelector(
  getSales,
  getSaleSearchBySearchField(SaleSearchFields.Status),
  getActiveRole,
  getSaleStatuses,
  (sales, filterValues, role, saleStatuses) => {
    const selectedSaleIds = filterValues
      ? Object.values(sales).reduce((result, sale) => {
          const { archetype, customStatus } = resolveSaleStatus(
            sale,
            role,
            saleStatuses,
          );
          if (
            filterValues.includes(customStatus?.id) ||
            filterValues.includes(archetype)
          ) {
            result.push(sale.livestocksale_id);
          }
          return result;
        }, [])
      : Object.values(sales).map(sale => sale.livestocksale_id);

    return selectedSaleIds;
  },
);

export const selectFilteredSaleIds = createSelector(
  [
    selectSaleIdFilteredSaleIds,
    selectSaleTitleFilteredSaleIds,
    selectSpeciesFilteredSaleIds,
    selectSaleyardFilteredSaleIds,
    selectSaleTypeFilteredSaleIds,
    selectSaleDateFilteredSaleIds,
    selectSaleStatusFilteredSaleIds,
    selectSaleManagersFilteredSaleIds,
    selectSaleAssigneesFilteredSaleIds,
    selectEContractStatusFilteredSaleIds,
  ],
  (...filterResults) => {
    const combinedSaleIds = spread(intersection)(filterResults);
    return combinedSaleIds;
  },
);

export const selectSaleIdsGroupedByPastAndFuture = createSelector(
  [getSales, selectFilteredSaleIds],
  (sales, filteredSaleIds) => {
    const pastSaleIds = [];
    const futureSaleIds = [];

    const filteredSales =
      filteredSaleIds && filteredSaleIds.length > 0
        ? filteredSaleIds.map(saleId => sales[saleId]).filter(Boolean)
        : [];

    const orderedSales = orderBy(filteredSales, "date", "desc");

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    Object.values(orderedSales).forEach(sale => {
      const saleDate = new Date(sale.date);
      if (saleDate < yesterday) {
        pastSaleIds.push(sale.livestocksale_id);
      } else {
        futureSaleIds.push(sale.livestocksale_id);
      }
    });

    return [pastSaleIds, futureSaleIds];
  },
);
